import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft,
  ArrowRight,
  CalendarCheck,
  Newspaper,
  CalendarClock,
  ShieldCheck,
  LockKeyhole,
} from "lucide-react";
import { toast } from "sonner";
import { useAdminToken } from "@/context/AdminTokenContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOOLS = [
  {
    to: "/admin/bookings",
    icon: CalendarCheck,
    title: "Bookings",
    body: "Paid & pending consultations, contact details, Meet links.",
    testId: "hub-tool-bookings",
  },
  {
    to: "/admin/blog",
    icon: Newspaper,
    title: "Blog",
    body: "Publish posts and broadcast them to newsletter subscribers.",
    testId: "hub-tool-blog",
  },
  {
    to: "/admin/calendar",
    icon: CalendarClock,
    title: "Google Calendar",
    body: "Connect the business Google account for auto Meet links.",
    testId: "hub-tool-calendar",
  },
];

const ToolCards = () => (
  <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="hub-tools">
    {TOOLS.map((t) => {
      const Icon = t.icon;
      return (
        <Link
          key={t.to}
          to={t.to}
          data-testid={t.testId}
          className="group bg-white border border-[var(--gg-border)] rounded-2xl p-6 hover:shadow-xl hover:-translate-y-1 transition-all"
        >
          <span className="w-12 h-12 rounded-full bg-[var(--gg-primary)]/10 flex items-center justify-center text-[var(--gg-primary)]">
            <Icon size={20} strokeWidth={1.8} />
          </span>
          <h3 className="font-serif text-xl text-[var(--gg-deep)] font-semibold mt-4">
            {t.title}
          </h3>
          <p className="text-sm text-[var(--gg-text-muted)] mt-2 leading-relaxed">
            {t.body}
          </p>
          <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[var(--gg-primary)] group-hover:text-[var(--gg-primary-dark)]">
            Open
            <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
          </span>
        </Link>
      );
    })}
  </div>
);

export default function AdminHubPage() {
  const { token, setToken } = useAdminToken();
  const [input, setInput] = useState("");
  const [checking, setChecking] = useState(false);

  const verify = async () => {
    if (!input) {
      toast.error("Enter your admin token.");
      return;
    }
    setChecking(true);
    try {
      await axios.get(`${API}/consultations?limit=1`, {
        headers: { "X-Admin-Token": input },
      });
      setToken(input);
      toast.success("Token verified — admin tools unlocked.");
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Invalid admin token.");
      } else {
        toast.error("Could not verify token. Please try again.");
      }
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="bg-[var(--gg-surface)] min-h-screen py-12" data-testid="admin-hub-page">
      <div className="max-w-4xl mx-auto px-6">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-[var(--gg-text-muted)] hover:text-[var(--gg-primary)] mb-6"
        >
          <ArrowLeft size={16} /> Back to site
        </Link>
        <h1 className="font-serif text-4xl text-[var(--gg-deep)] font-semibold">
          Graceful Guidance — Admin
        </h1>
        <p className="text-[var(--gg-text)] mt-2">
          One place for bookings, blog publishing, and calendar setup.
        </p>

        {token ? (
          <>
            <div
              className="mt-8 bg-white border border-emerald-200 rounded-2xl p-5 flex items-center gap-4"
              data-testid="hub-unlocked"
            >
              <span className="w-11 h-11 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700">
                <ShieldCheck size={18} />
              </span>
              <div className="flex-1">
                <div className="font-serif text-lg text-[var(--gg-deep)] font-semibold">
                  Admin tools unlocked
                </div>
                <div className="text-xs text-[var(--gg-text-muted)]">
                  Your token is shared across all admin pages for this browser session.
                </div>
              </div>
              <button
                onClick={() => setToken("")}
                data-testid="hub-clear-token"
                className="text-sm text-[var(--gg-text-muted)] hover:text-red-600 underline"
              >
                Clear token
              </button>
            </div>
            <ToolCards />
          </>
        ) : (
          <div className="mt-8 bg-white border border-[var(--gg-border)] rounded-2xl p-6" data-testid="hub-locked">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-11 h-11 rounded-full bg-[var(--gg-primary)]/10 flex items-center justify-center text-[var(--gg-primary)]">
                <LockKeyhole size={18} />
              </span>
              <div className="font-serif text-xl text-[var(--gg-deep)] font-semibold">
                Enter your admin token once
              </div>
            </div>
            <Label className="text-xs uppercase tracking-[0.18em] text-[var(--gg-text-muted)] font-semibold">
              Admin token
            </Label>
            <div className="flex gap-2 mt-2">
              <Input
                type="password"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && verify()}
                placeholder="Paste your X-Admin-Token"
                data-testid="hub-token-input"
              />
              <button
                onClick={verify}
                disabled={checking}
                className="bg-[var(--gg-primary)] hover:bg-[var(--gg-primary-dark)] disabled:opacity-60 text-white rounded-full px-6 text-sm whitespace-nowrap"
                data-testid="hub-token-verify"
              >
                {checking ? "Verifying…" : "Unlock"}
              </button>
            </div>
            <p className="text-xs text-[var(--gg-text-muted)] mt-2">
              Held in memory only — never stored in the browser. It unlocks
              Bookings, Blog, and Calendar in one go.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
